import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProperty } from '../hooks/useProperty';
import { useTenants } from '../hooks/useTenants';
import SmartUploader from '../components/SmartUploader';

export default function UploadPage() {
  const { properties, selectedProperty, setSelectedProperty, addProperty, updateProperty } = useProperty();
  const { addTenants } = useTenants(selectedProperty?.id);
  const [uploading, setUploading] = useState(false);
  const [newPropName, setNewPropName] = useState('');
  const [newPropUnits, setNewPropUnits] = useState('');
  const [showNewProp, setShowNewProp] = useState(false);
  const [success, setSuccess] = useState(false);
  const [importCount, setImportCount] = useState(0);
  const navigate = useNavigate();

  const handleCreateProperty = async (event) => {
    event.preventDefault();
    if (!newPropName.trim()) {
      return;
    }

    const newProperty = await addProperty({
      name: newPropName.trim(),
      units: parseInt(newPropUnits, 10) || 0,
      address: '',
    });

    setSelectedProperty({ id: newProperty.id, name: newPropName.trim() });
    setShowNewProp(false);
    setNewPropName('');
    setNewPropUnits('');
  };

  const handleDataParsed = async (tenants, documentSummary) => {
    if (!selectedProperty?.id) {
      alert('Please select or create a property first.');
      return;
    }

    setUploading(true);
    try {
      await addTenants(tenants);

      if (documentSummary) {
        await updateProperty(selectedProperty.id, {
          lastDataIntake: {
            type: documentSummary.type,
            label: documentSummary.label,
            confidence: documentSummary.confidence,
            note: documentSummary.note,
            sourceSystem: documentSummary.sourceSystem,
            sourceSystemLabel: documentSummary.sourceSystemLabel,
            dataCoverage: documentSummary.dataCoverage,
            dataCoverageLabel: documentSummary.dataCoverageLabel,
            dataCoverageGuidance: documentSummary.dataCoverageGuidance,
            recommendedMode: documentSummary.recommendedMode,
            supportsTenantImport: documentSummary.supportsTenantImport,
            nextBestUpload: documentSummary.nextBestUpload,
            signals: documentSummary.signals || [],
            fileName: documentSummary.fileName || '',
            sheetName: documentSummary.sheetName || '',
            rowCount: documentSummary.rowCount || tenants.length,
            importedTenantCount: tenants.length,
            importedAt: new Date().toISOString(),
          },
        });
      }

      setImportCount(tenants.length);
      setSuccess(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      alert(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Upload Property Data</h1>
        <p className="text-gray-500 mb-8">
          Upload rent rolls, delinquency exports, ledgers, and operating statements from your PMS. Tenafi will identify the document type, tell you what it unlocks, and only import tenant-level files when the structure is safe.
        </p>

        <div className="grid md:grid-cols-4 gap-3 mb-6">
          {[
            { title: 'Rent Rolls', text: 'Best for tenant creation, balances, and current scoring.' },
            { title: 'Delinquency Reports', text: 'Great for past-due balances and action prioritization.' },
            { title: 'Resident Ledgers', text: 'Best source for payment history, partial pays, and drift over time once linked to the roster.' },
            { title: 'T12 / Financials', text: 'Detected for analytics review so they do not get imported as tenants by mistake.' },
          ].map((card) => (
            <div key={card.title} className="bg-white rounded-2xl border border-gray-200 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">{card.title}</p>
              <p className="text-sm text-gray-600 leading-relaxed">{card.text}</p>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-3 mb-6">
          {[
            {
              title: 'Start Now',
              text: 'One current rent roll or delinquency snapshot is enough to build a baseline Action Feed and start outreach.',
            },
            {
              title: 'Better Scoring',
              text: 'Three to six months of delinquency or resident ledger history materially improves trend detection and expected pay-date confidence.',
            },
            {
              title: 'Best Version',
              text: 'A PMS integration plus ledgers, contact data, and payment updates is the path to stronger Autopilot, voice, and portfolio analytics.',
            },
          ].map((card) => (
            <div key={card.title} className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-1">{card.title}</p>
              <p className="text-sm text-blue-900 leading-relaxed">{card.text}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">Step 1: Select Property</h2>

          {properties.length > 0 && !showNewProp ? (
            <div className="flex items-center gap-3">
              <select
                value={selectedProperty?.id || ''}
                onChange={(event) => {
                  const property = properties.find((item) => item.id === event.target.value);
                  setSelectedProperty(property);
                }}
                className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-tenafi-green outline-none"
              >
                <option value="">Select a property...</option>
                {properties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setShowNewProp(true)}
                className="px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50"
              >
                + New
              </button>
            </div>
          ) : (
            <form onSubmit={handleCreateProperty} className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">Property Name</label>
                <input
                  type="text"
                  value={newPropName}
                  onChange={(event) => setNewPropName(event.target.value)}
                  placeholder="e.g. Sunrise Apartments"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-tenafi-green outline-none"
                  required
                />
              </div>
              <div className="w-28">
                <label className="block text-sm text-gray-600 mb-1">Units</label>
                <input
                  type="number"
                  value={newPropUnits}
                  onChange={(event) => setNewPropUnits(event.target.value)}
                  placeholder="200"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-tenafi-green outline-none"
                />
              </div>
              <button
                type="submit"
                className="px-5 py-3 bg-tenafi-green text-white rounded-xl text-sm font-semibold hover:bg-tenafi-green-dark"
              >
                Create
              </button>
              {properties.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowNewProp(false)}
                  className="px-4 py-3 text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              )}
            </form>
          )}
        </div>

        <div className={`bg-white rounded-2xl border border-gray-200 p-6 ${!selectedProperty ? 'opacity-50 pointer-events-none' : ''}`}>
          <h2 className="font-semibold text-gray-900 mb-4">Step 2: Upload Property File</h2>
          <SmartUploader key={selectedProperty?.id} onDataParsed={handleDataParsed} isLoading={uploading} />
        </div>

        {success && (
          <div className="mt-6 bg-green-50 border border-green-200 rounded-2xl p-5 text-center">
            <div className="text-3xl mb-2">Done</div>
            <p className="font-semibold text-green-800">Upload successful! {importCount} tenants imported.</p>
            <p className="text-green-600 text-sm mt-1">Redirecting to your Action Feed...</p>
          </div>
        )}
      </div>
    </div>
  );
}
